import { Link, useNavigate } from "react-router";
import { notifications as api } from "../api/client";
import type { Notification } from "../api/types";
import { useMe, companyCanWrite } from "../auth/session";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Pill, Spinner, useToast, errorMessage } from "../components/ui";
import { useAsync, usePolling } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateTimeShort } from "../lib/format";

export function Notificacoes() {
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, error, loading, reload, setData } = useAsync(() => api.list(), []);
  usePolling(() => reload(), 30_000, true);
  const items: Notification[] = data ?? [];
  const unread = items.filter((n) => !n.readAt).length;
  const canWrite = companyCanWrite(me);

  async function open(n: Notification) {
    if (!n.readAt && canWrite) {
      setData((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      api.read(n.id).catch(() => {});
    }
    if (n.orderId) navigate(`/pagamentos/${n.orderId}`);
  }

  async function markAll() {
    try {
      await api.readAll();
      setData((prev) => (prev ?? []).map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })));
      toast(t.notifications.markedAll);
    } catch (e) {
      toast(errorMessage(e));
    }
  }

  return (
    <Page
      title={t.notifications.title}
      crumb={unread ? fmt(t.notifications.unread, { n: unread }) : undefined}
      actions={unread > 0 && canWrite ? <button type="button" className="btn ghost" onClick={markAll}>{t.notifications.markAll}</button> : undefined}
    >
      <div className="card">
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : items.length === 0 ? (
          <Empty body={t.notifications.empty} />
        ) : (
          <ul className="notif-list">
            {items.map((n) => (
              <li key={n.id} className={n.readAt ? "" : "unread"}>
                <button type="button" onClick={() => open(n)}>
                  <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                      <b>{n.subject}</b>
                      {!n.readAt && <Pill tone="go">{t.notifications.newTag}</Pill>}
                    </div>
                    <span className="ink2" style={{ fontSize: 13.5 }}>{n.body}</span>
                    <span className="muted small">{dateTimeShort(n.createdAt)}{n.orderId ? ` · ${n.orderId}` : ""}</span>
                  </div>
                  {n.orderId && <span className="linkbtn">{t.notifications.openOrder}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="muted small" style={{ marginTop: 10 }}>
        <Link to="/configuracoes">{t.settings.push}</Link>
      </p>
    </Page>
  );
}
